/**
 * Compact participants panel for the SessionPage sidebar. Replaces the
 * previous "Participants →" link that navigated to a separate page —
 * the operator now manages pending invites + sees joined participants
 * inline next to the chat.
 *
 * Layout is tight (sidebar is `w-80` ≈ 320px): each row stacks the
 * meta on top, action buttons below. The edit form for pending
 * invites lays inputs out vertically to fit the narrow column.
 *
 * The full DSR-actions surface (Export / Erase) is intentionally NOT
 * surfaced here — those belong on the standalone admin page where
 * compliance reviewers actually work. The sidebar's job is operational:
 * resend, edit-and-resend, or cancel a pending invite while the
 * session is still live.
 *
 * When the session is in a terminal state (closed / expired), the
 * panel renders read-only: the roster is still visible, but the
 * Resend / Edit / Cancel affordances disappear (per user instruction
 * — once the session is over, re-issuing an invite makes no sense).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import PhoneInput from "@/components/PhoneInput";
import { api } from "@/lib/api";
import { displayPhone } from "@/lib/phone";
import { fetchSessionParticipants, type SessionParticipant } from "@/modules/dsr";


type Props = {
  sessionId: string;
  /** True when the session is closed/expired. Hides the action buttons
   *  on pending invites — the operator can't usefully re-invite once
   *  the session is over. */
  terminal: boolean;
  /** Redesign 2026-09-23 — the field user (the beneficiary) reports
   *  their own presence on the beneficiary card now, so this panel
   *  covers everyone ELSE in the room. Showing the beneficiary in both
   *  places was the duplication the operator had to reconcile. The full
   *  list, departed rows included, stays on the DSR/admin surface — it
   *  is evidence, not decoration. */
  hideFieldUsers?: boolean;
  /** Whether the disclosure is open by default. Defaults to false so
   *  Chat (the primary surface) retains its vertical space until the
   *  operator actively opens this panel. */
  defaultOpen?: boolean;
};


export function ParticipantsSidebarPanel({
  sessionId,
  terminal,
  hideFieldUsers = false,
  defaultOpen = false,
}: Props) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["session-participants", sessionId, "with-pending"],
    queryFn: () => fetchSessionParticipants(sessionId, { includePending: true }),
    // Newly-issued invites from the InviteFieldUserModal should appear
    // here within a few seconds without a hard refresh.
    refetchInterval: 10000,
    staleTime: 4000,
  });

  const all = q.data ?? [];
  const rows = hideFieldUsers ? all.filter((p) => p.role !== "field") : all;
  const pendingCount = rows.filter((p) => p.pending).length;
  // Reported 2026-09-23 — this counted everyone who was not pending, so
  // a beneficiary who joined and left still showed a green "1 joined"
  // over an empty room. Whether someone is HERE is the question this
  // badge is asked; who has been and gone is a separate fact.
  const inCallCount = rows.filter((p) => !p.pending && !p.left_at).length;
  const leftCount = rows.filter((p) => !p.pending && !!p.left_at).length;

  if (hideFieldUsers && rows.length === 0) return null;

  return (
    <details
      className="group rounded-md border border-nb-border bg-slate-900/40"
      open={defaultOpen}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-nb-text-2 hover:bg-slate-800/40">
        <span>{t("session.participants")}</span>
        <span className="flex items-center gap-1.5 text-[10px] font-normal normal-case tracking-normal text-nb-text-3">
          {inCallCount > 0 && (
            <span className="rounded bg-nb-mint/15 px-1.5 py-0.5 text-nb-mint">
              {t("session.participantsInCall", {
                count: inCallCount,
                defaultValue: "{{count}} in call",
              })}
            </span>
          )}
          {leftCount > 0 && (
            <span className="rounded bg-slate-500/20 px-1.5 py-0.5 text-nb-text-3">
              {t("session.participantsLeft", {
                count: leftCount,
                defaultValue: "{{count}} left",
              })}
            </span>
          )}
          {pendingCount > 0 && (
            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-300">
              {t("session.participantsPending", {
                count: pendingCount,
                defaultValue: "{{count}} pending",
              })}
            </span>
          )}
          <svg
            className="h-3 w-3 transition-transform group-open:rotate-180"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M3 4.5l3 3 3-3" />
          </svg>
        </span>
      </summary>
      <div className="max-h-72 overflow-y-auto border-t border-nb-border px-2 py-2">
        {q.isLoading ? (
          <p className="px-1 py-2 text-xs text-nb-text-3">Loading…</p>
        ) : q.isError ? (
          <p className="px-1 py-2 text-xs text-cvp-danger">
            Could not load participants
          </p>
        ) : rows.length === 0 ? (
          <p className="px-1 py-2 text-xs text-nb-text-3">
            No participants yet. They appear here once invited or once
            they join the room.
          </p>
        ) : (
          <ul className="divide-y divide-nb-border">
            {rows.map((p) => (
              <li key={p.id} className="py-2">
                <ParticipantRow
                  sessionId={sessionId}
                  participant={p}
                  terminal={terminal}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}


function ParticipantRow({
  sessionId,
  participant: p,
  terminal,
}: {
  sessionId: string;
  participant: SessionParticipant;
  terminal: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className="rounded bg-nb-mint/15 px-1.5 py-0.5 text-[10px] uppercase text-nb-mint">
          {p.role}
        </span>
        <span
          className={`truncate text-xs font-medium ${p.erased ? "italic text-nb-text-3" : "text-nb-text"}`}
          title={p.id}
        >
          {p.display_name || t("participantsPanel.unnamed")}
          {/* Short LiveKit identity as a secondary reference — lets
              support/ops correlate this row against the LiveKit room
              dashboard without it reading as the participant's name
              (backlog: "participant:8fcd8f78" was showing AS the
              name; display_name is now always resolved server-side,
              but the identity stays visible in brackets either way). */}
          <span className="ml-1 font-normal text-nb-text-3">
            ({p.id.slice(0, 8)})
          </span>
        </span>
        {p.pending && (
          <span className="rounded bg-amber-500/15 px-1 py-0.5 text-[9px] font-medium text-amber-300">
            {t("participantsPanel.invited")}
          </span>
        )}
        {p.erased && (
          <span className="text-[10px] text-nb-text-3">{t("participantsPanel.tombstoned")}</span>
        )}
      </div>
      <div className="text-[10px] text-nb-text-3">
        {p.pending ? (
          <>
            {t("participantsPanel.inviteSent")} · {formatDateTime(p.created_at)}
            {p.recipient_email && <> · {p.recipient_email}</>}
            {!p.recipient_email && p.recipient_phone && (
              <> · {displayPhone(p.recipient_phone)}</>
            )}
          </>
        ) : (
          <>
            {t("participantsPanel.joined")}{" "}
            {p.joined_at ? formatDateTime(p.joined_at) : t("participantsPanel.never")}
            {p.left_at && <> · {t("participantsPanel.left")} {formatDateTime(p.left_at)}</>}
          </>
        )}
      </div>
      {/* Action affordances only for pending invites, only while the
          session is still live. Joined participants don't expose DSR
          actions here — those live on the standalone admin page. */}
      {p.pending && !terminal && (
        <PendingInviteActions sessionId={sessionId} invite={p} />
      )}
    </div>
  );
}


function PendingInviteActions({
  sessionId,
  invite,
}: {
  sessionId: string;
  invite: SessionParticipant;
}) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(invite.recipient_first_name ?? "");
  const [last, setLast] = useState(invite.recipient_last_name ?? "");
  const [email, setEmail] = useState(invite.recipient_email ?? "");
  const [phone, setPhone] = useState(invite.recipient_phone ?? "");
  const [feedback, setFeedback] = useState<string | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["session-participants", sessionId, "with-pending"],
    });

  const resend = useMutation({
    mutationFn: () =>
      api.post<{ channels: string[] }>(
        `/v1/sessions/${sessionId}/invites/${invite.id}/resend`,
      ),
    onSuccess: (res) => {
      setFeedback(t("participantsPanel.resentVia", { channels: res.data.channels.join(" + ") }));
    },
    onError: (e) => {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t("participantsPanel.couldNotResend");
      setFeedback(detail);
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.delete(`/v1/sessions/${sessionId}/invites/${invite.id}`),
    onSuccess: () => {
      void invalidate();
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/v1/sessions/${sessionId}/invites/${invite.id}`, {
        recipient_first_name: first,
        recipient_last_name: last,
        recipient_email: email,
        recipient_phone: phone,
      }),
    onSuccess: () => {
      setEditing(false);
      setFeedback(t("participantsPanel.saved"));
      void invalidate();
    },
    onError: (e) => {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? t("participantsPanel.couldNotSave");
      setFeedback(detail);
    },
  });

  if (editing) {
    return (
      <div className="mt-1 space-y-1 rounded-md border border-nb-border bg-slate-900/60 p-1.5">
        <div className="grid grid-cols-2 gap-1">
          <input
            className="input text-[11px]"
            placeholder={t("participantsPanel.firstPlaceholder")}
            value={first}
            onChange={(e) => setFirst(e.target.value)}
          />
          <input
            className="input text-[11px]"
            placeholder={t("participantsPanel.lastPlaceholder")}
            value={last}
            onChange={(e) => setLast(e.target.value)}
          />
        </div>
        <input
          className="input text-[11px]"
          type="email"
          placeholder={t("participantsPanel.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PhoneInput
          className="text-[11px]"
          value={phone}
          onChange={setPhone}
        />
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="btn-ghost text-[11px]"
            onClick={() => setEditing(false)}
            disabled={save.isPending}
          >
            {t("participantsPanel.editCancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-[11px]"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? t("participantsPanel.saving") : t("participantsPanel.save")}
          </button>
        </div>
        {feedback && (
          <p className="text-right text-[10px] text-nb-text-3">{feedback}</p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {invite.join_url && (
        <button
          type="button"
          className="btn-ghost px-1.5 py-0.5 text-[11px]"
          onClick={() => {
            void navigator.clipboard
              ?.writeText(invite.join_url ?? "")
              .then(() => setFeedback(t("participantsPanel.linkCopied")))
              .catch(() => setFeedback(t("participantsPanel.copyFailed")));
          }}
          title={t("participantsPanel.copyLinkTitle")}
        >
          {t("participantsPanel.copyLink")}
        </button>
      )}
      <button
        type="button"
        className="btn-ghost px-1.5 py-0.5 text-[11px]"
        onClick={() => resend.mutate()}
        disabled={resend.isPending}
        title={t("participantsPanel.resendTitle")}
      >
        {resend.isPending ? t("participantsPanel.resending") : t("participantsPanel.resend")}
      </button>
      <button
        type="button"
        className="btn-ghost px-1.5 py-0.5 text-[11px]"
        onClick={() => {
          setFirst(invite.recipient_first_name ?? "");
          setLast(invite.recipient_last_name ?? "");
          setEmail(invite.recipient_email ?? "");
          setPhone(invite.recipient_phone ?? "");
          setEditing(true);
        }}
        title={t("participantsPanel.editTitle")}
      >
        {t("participantsPanel.edit")}
      </button>
      <button
        type="button"
        className="btn-ghost px-1.5 py-0.5 text-[11px] text-cvp-danger hover:bg-cvp-danger/10"
        onClick={() => {
          if (
            confirm(
              t("participantsPanel.cancelInviteConfirm"),
            )
          ) {
            cancel.mutate();
          }
        }}
        disabled={cancel.isPending}
        title={t("participantsPanel.expireTitle")}
      >
        {cancel.isPending ? t("participantsPanel.cancelling") : t("participantsPanel.cancelInvite")}
      </button>
      {feedback && (
        <span className="ml-auto text-[10px] text-nb-text-3">{feedback}</span>
      )}
    </div>
  );
}
